/**
 * Hook para buscar dados de todas as páginas do Google Analytics 4
 */
import { useState, useEffect } from "react";
import { getAllPagesApiV1PagesAllPagesGet } from "@/backend-sdk/client/sdk.gen";
import type {
  PagesAllPagesItem,
  PagesAllPagesTotals,
} from "@/backend-sdk/client/types.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import type { TableQueryDTO } from "@/types/table-query";

type AllPagesData = PagesAllPagesItem;
type AllPagesTotals = PagesAllPagesTotals;

export const useAllPagesData = (
  startDate: string,
  endDate: string,
  comparison: string,
  customerUuid?: string,
  accountId?: string,
  page: number = 1,
  pageSize: number = 10,
  tableQuery?: TableQueryDTO,
  campaignFiltersNonce?: number,
) => {
  const [data, setData] = useState<AllPagesData[]>([]);
  const [totals, setTotals] = useState<AllPagesTotals | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!customerUuid) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const { data: response } = await getAllPagesApiV1PagesAllPagesGet({
          query: {
            start_date: startDate,
            end_date: endDate,
            comparison,
            customer_uuid: customerUuid,
            account_id: accountId,
            page,
            page_size: pageSize,
            q: tableQuery?.q,
            sort_by: tableQuery?.sortBy,
            sort_dir: tableQuery?.sortDir,
            filters: tableQuery?.filters?.length
              ? JSON.stringify(tableQuery.filters)
              : undefined,
          },
          throwOnError: true,
        });

        setData(response.data || []);
        setTotals(response.totals || null);
        setPagination(
          response.pagination || {
            page: 1,
            page_size: 10,
            total_items: 0,
            total_pages: 0,
          },
        );
      } catch (err: unknown) {
        console.error("Erro ao buscar dados de páginas:", err);
        setError(
          isHeyApiError(err) && typeof err.detail === "string"
            ? err.detail
            : "Erro ao buscar dados de páginas",
        );
        setData([]);
        setTotals(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    startDate,
    endDate,
    comparison,
    customerUuid,
    accountId,
    page,
    pageSize,
    tableQuery,
    campaignFiltersNonce,
  ]);

  return {
    data,
    totals,
    pagination,
    loading,
    error,
  };
};
